import React, { Suspense, lazy, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, LayoutDashboard, TrendingUp, Users, Tag } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '../../AuthContext';
import { useBilling } from '../../contexts/BillingContext';
import {
  canUseStripeBillingControls,
  isAppleManagedBilling,
  isManagedByOwnerBilling,
} from '../../utils/billingStatusState';
import Button from '../ui/Button';
import OnboardingHeader from '../onboarding/OnboardingHeader';
import { AppWorkspaceScene } from '../onboarding/OnboardingVisuals';
import { getMonthlyPriceDisplay } from '../../utils/billingCopy';

const PaymentMethodModal = lazy(() => import('./PaymentMethodModal'));

// ─── Features list (right column) ────────────────────────────────────────────

const FEATURES = [
  { icon: LayoutDashboard, label: 'Spending dashboard & net worth' },
  { icon: TrendingUp,      label: 'Budget tracking & insights' },
  { icon: Tag,             label: 'Auto-categorization' },
  { icon: Users,           label: 'Shared household finances' },
];

// ─── TrialExpiredPaywall ──────────────────────────────────────────────────────

const TrialExpiredPaywall = ({ mode = 'trial_expired' }) => {
  const { logout } = useAuth();
  const { billingStatus, refresh } = useBilling();
  const navigate = useNavigate();
  const location = useLocation();
  const isLight = false;

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const isMobileRoute = location.pathname === '/m' || location.pathname.startsWith('/m/');
  const settingsPath = isMobileRoute ? '/m/settings?section=billing' : '/settings?section=billing';

  const isCanceled = mode === 'canceled' || billingStatus?.billingState === 'canceled';
  const isPaymentIssue = mode === 'payment_issue';
  const isBillingUnavailable = mode === 'billing_unavailable';
  const isSubscriptionRequired = mode === 'subscription_required';
  const billingOwnerName = billingStatus?.billingOwnerName || 'the household owner';
  const isAppleManaged = isAppleManagedBilling(billingStatus);
  const isReadOnlyMemberBilling = isManagedByOwnerBilling(billingStatus);
  const isMemberSubscriptionRequired = isSubscriptionRequired && isReadOnlyMemberBilling;
  const canUseStripeControls = canUseStripeBillingControls(billingStatus);
  const isReadOnlyRecovery = isAppleManaged || isReadOnlyMemberBilling;
  const priceDisplay = billingStatus?.priceDisplay || getMonthlyPriceDisplay(billingStatus?.currency || 'USD');

  const eyebrow = isBillingUnavailable
    ? 'Billing unavailable'
    : isSubscriptionRequired
    ? 'Subscription required'
    : isAppleManaged
    ? (isPaymentIssue ? 'Billing issue' : isCanceled ? 'Subscription ended' : 'Trial ended')
    : isReadOnlyMemberBilling
    ? (isPaymentIssue ? 'Billing issue' : isCanceled ? 'Subscription ended' : 'Trial ended')
    : isPaymentIssue
    ? 'Payment issue'
    : isCanceled
    ? 'Subscription ended'
    : 'Trial ended';
  const headline = isBillingUnavailable
    ? 'We couldn’t verify billing right now'
    : isSubscriptionRequired
    ? (isMemberSubscriptionRequired ? 'Your household needs a subscription' : 'Subscribe to continue')
    : isAppleManaged
    ? (isPaymentIssue
        ? 'Your access is paused'
        : isCanceled
          ? 'Your subscription is inactive'
          : 'Your trial has ended')
    : isReadOnlyMemberBilling
    ? (isPaymentIssue
        ? 'Your household access is paused'
        : isCanceled
          ? 'Your household subscription is inactive'
          : 'Your household free trial has ended')
    : isPaymentIssue
    ? 'We couldn’t process your payment'
    : isCanceled
    ? 'Your subscription has ended'
    : 'Your trial has ended';
  const subline = isBillingUnavailable
    ? 'Your data is safe. Try again in a moment. If this keeps happening, open Billing in Settings once the connection is back.'
    : isSubscriptionRequired
    ? (isMemberSubscriptionRequired
        ? `${billingOwnerName} needs to subscribe to continue using Vylo.`
        : `Subscribe to use Vylo for ${priceDisplay}.`)
    : isAppleManaged
    ? (
      isPaymentIssue
        ? 'Update your App Store payment method to restore access. Your data is safe.'
        : 'Manage or restore your Vylo subscription in the App Store. Your data is safe.'
    )
    : isReadOnlyMemberBilling
    ? (isPaymentIssue
        ? `${billingOwnerName} needs to update billing to restore access. Billing can only be managed by the household owner.`
        : isCanceled
          ? `${billingOwnerName} needs to resubscribe to restore access for your household.`
          : `${billingOwnerName} needs to update billing to restore access for your household.`)
    : isPaymentIssue
    ? 'Your data is safe. Update your payment method to restore Vylo.'
    : isCanceled
    ? 'Resubscribe to restore Vylo. Your data is right where you left it.'
    : `Subscribe to keep using Vylo for ${priceDisplay}. Your data is safe.`;
  const ctaLabel = isBillingUnavailable
    ? 'Try again'
    : isSubscriptionRequired
    ? (isMemberSubscriptionRequired ? 'View Billing' : 'Subscribe')
    : isReadOnlyRecovery
    ? 'View Billing'
    : isPaymentIssue
    ? 'Update payment method'
    : isCanceled
    ? 'Resubscribe'
    : 'Subscribe';
  const supportHeadline = isBillingUnavailable
    ? 'What happens next'
    : isPaymentIssue
    ? 'What will be restored'
    : isCanceled
    ? "What's included"
    : "What's included";

  const handlePrimaryAction = async () => {
    if (isBillingUnavailable) {
      if (retrying) return;
      setRetrying(true);
      try {
        await refresh();
      } finally {
        setRetrying(false);
      }
      return;
    }
    if (isReadOnlyRecovery || !canUseStripeControls) {
      navigate(settingsPath);
      return;
    }
    setPaymentModalOpen(true);
  };

  // ─── Style tokens (match onboarding exactly) ──────────────────────────────

  const pageBackgroundStyle = {
    background: isLight
      ? 'linear-gradient(180deg, #f5f8fd 0%, #e7edf6 100%)'
      : 'linear-gradient(180deg, #0b1320 0%, #0e1726 100%)',
  };

  const overlayCardClassName = isLight
    ? 'relative overflow-hidden rounded-[32px] border border-white/90 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.14)]'
    : 'relative overflow-hidden rounded-[32px] border border-white/[0.08] bg-[#121b2b] shadow-[0_28px_100px_rgba(2,6,23,0.55)]';

  const supportPanelClassName = isLight
    ? 'rounded-[28px] border border-slate-900/8 bg-slate-50 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.06)]'
    : 'rounded-[28px] border border-white/[0.08] bg-[#182336] p-5 shadow-[0_20px_48px_rgba(2,6,23,0.24)]';

  const eyebrowClassName = isLight
    ? 'inline-flex self-start items-center rounded-full border border-amber-500/20 bg-amber-500/8 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700'
    : 'inline-flex self-start items-center rounded-full border border-amber-400/25 bg-amber-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-300';

  const primaryTextClassName = isLight ? 'text-slate-950' : 'text-white';
  const secondaryTextClassName = isLight ? 'text-slate-700' : 'text-slate-300';
  const mutedTextClassName = isLight ? 'text-slate-500' : 'text-slate-400';
  const featureIconClassName = isLight ? 'text-indigo-500' : 'text-indigo-400';

  return (
    <div
      className="expense-tracker-shell relative min-h-screen overflow-hidden"
      style={pageBackgroundStyle}
    >
      {/* Blurred app workspace backdrop — identical to onboarding */}
      <div aria-hidden className="absolute inset-0">
        <motion.div
          initial={{ opacity: 0.72, scale: 1.025 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0"
        >
          <div className="absolute inset-0 scale-[1.01] opacity-[0.92] blur-[9px] saturate-[0.92]">
            <AppWorkspaceScene focus="overview" />
          </div>
        </motion.div>
        <div className={`absolute inset-0 ${
          isLight
            ? 'bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(231,237,246,0.18))]'
            : 'bg-[linear-gradient(180deg,rgba(11,19,32,0.10),rgba(11,19,32,0.20))]'
        }`} />
      </div>

      {/* Content */}
      <div className="relative z-10 px-4 pb-4 pt-2">
        <div className="mx-auto flex min-h-[calc(100vh-2rem)] max-w-7xl flex-col">
          <OnboardingHeader forceDark />

          <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center px-4 py-5 sm:px-6 sm:py-6 lg:px-10 lg:py-8">
            <div className="w-full max-w-5xl">
              <AnimatePresence mode="wait">
                <motion.div
                  key="paywall-card"
                  initial={{ opacity: 0, y: 18, scale: 0.985 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.985 }}
                  transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                  className={overlayCardClassName}
                >
                  <div className="relative grid gap-6 p-6 sm:p-8 lg:grid-cols-[minmax(0,1.06fr)_minmax(360px,0.94fr)] lg:items-center lg:gap-8">

                    {/* ── Left column ─────────────────────────────────── */}
                    <div className="flex flex-col gap-7 lg:pr-2">
                      <div className="space-y-6">
                        {/* Eyebrow */}
                        <p className={eyebrowClassName}>{eyebrow}</p>

                        {/* Headline + subline */}
                        <div className="space-y-4">
                          <h2 className={`text-[clamp(1.75rem,2.5vw,2.25rem)] font-bold leading-[1.1] ${primaryTextClassName}`}>
                            {headline}
                          </h2>
                          <p className={`max-w-[32rem] text-[clamp(0.9rem,1.1vw,1.05rem)] leading-7 ${secondaryTextClassName}`}>
                            {subline}
                          </p>
                        </div>
                      </div>

                      {/* CTAs */}
                      <div className="max-w-[26rem] space-y-3">
                        <Button
                          size="lg"
                          fullWidth
                          onClick={handlePrimaryAction}
                          loading={retrying}
                          loadingLabel="Checking access…"
                        >
                          {ctaLabel}
                        </Button>

                        <div className={`flex items-center justify-center gap-4 pt-1 text-sm ${mutedTextClassName}`}>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={logout}
                            className={`transition-colors ${isLight ? 'hover:text-slate-800' : 'hover:text-white'}`}
                          >
                            Sign out
                          </Button>
                          <span className="opacity-30">·</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(settingsPath)}
                            className={`transition-colors ${isLight ? 'hover:text-slate-800' : 'hover:text-white'}`}
                          >
                            View Settings
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* ── Right column ─────────────────────────────────── */}
                    <div className={`${supportPanelClassName} flex flex-col gap-8 lg:self-center`}>
                      <div className="space-y-5">
                        <p className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${mutedTextClassName}`}>
                          {supportHeadline}
                        </p>
                        <div className="space-y-5">
                          {FEATURES.map(({ icon: Icon, label }) => (
                            <div key={label} className="flex items-center gap-3">
                              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${
                                isLight ? 'bg-indigo-500/8' : 'bg-indigo-500/12'
                              }`}>
                                <Icon className={`h-4 w-4 ${featureIconClassName}`} />
                              </div>
                              <span className={`text-sm font-medium ${primaryTextClassName}`}>{label}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className={`rounded-2xl border p-4 ${
                        isLight
                          ? 'border-emerald-500/20 bg-emerald-500/5'
                          : 'border-emerald-400/15 bg-emerald-500/[0.06]'
                      }`}>
                        <div className="flex items-start gap-2.5">
                          <Check className={`mt-0.5 h-4 w-4 shrink-0 ${isLight ? 'text-emerald-600' : 'text-emerald-400'}`} />
                          <p className={`text-sm leading-relaxed ${secondaryTextClassName}`}>
                            Cancel anytime.
                          </p>
                        </div>
                      </div>
                    </div>

                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {!isBillingUnavailable && canUseStripeControls && paymentModalOpen ? (
        <Suspense fallback={null}>
          <PaymentMethodModal
            isOpen={paymentModalOpen}
            onClose={() => setPaymentModalOpen(false)}
            successPresentation="overlay"
          />
        </Suspense>
      ) : null}
    </div>
  );
};

export default TrialExpiredPaywall;
